import m, {MithrilComponent, type ComponentType} from '@bitstillery/mithril'
import {AuthForgotPassword, AuthResetPassword, Button, Dialog, Tab, TabPanel, Tabs} from '@bitstillery/common/components'
import {$t, $s, api, config, notifier} from '@bitstillery/common/app'
import {is_one_of, plausible_event} from '@bitstillery/common/lib/utils'
import {set_language} from '@bitstillery/common/lib/i18n'

import type {ErrorResponse, PortalLoginResponse, VerifyOTPRequest} from '@bitstillery/common/api/fact2server'
import {AccountSlug, LoginErrorMessage} from '@bitstillery/common/api/fact2server'
import {AuthLogin as AuthLoginCommon} from '@bitstillery/common/components'
import {PlausibleEvent} from '@bitstillery/common/types/utils'

// ---------------------------------------------------------------------------
// Auth routes — the dialog is route-driven: each view has its own URL so it is
// a deep-linkable, refreshable, back/forward-navigable route. The CMS home page
// ('/') is rendered behind the dialog (CmsDynamicPage loads '/' for these routes).
// ---------------------------------------------------------------------------

export const AUTH_PATHS = {
    login: '/login',
    register: '/register',
    forgot: '/forgot-password',
    reset: '/reset-password',
} as const

export const AUTH_ROUTES: readonly string[] = Object.values(AUTH_PATHS)

export function is_auth_route(uri: string): boolean {
    return AUTH_ROUTES.includes(uri)
}

/**
 * The post-auth `?redirect` target, read from the raw query string. `m.route.param('redirect')`
 * can't be used: the router decodes the whole URL before parsing, so a target that itself carries
 * a query string (`/shop?availability=tbo`) splits on the now-literal `=` and collapses to ''.
 */
function redirect_target(): string | undefined {
    const search = typeof window === 'undefined' ? '' : window.location.search
    return new URLSearchParams(search).get('redirect') ?? undefined
}

/** Append the current `?redirect` param so it survives tab/sub-view navigation inside the dialog. */
function auth_href(path: string): string {
    const redirect = redirect_target()
    return redirect ? `${path}?redirect=${encodeURIComponent(redirect)}` : path
}

// ---------------------------------------------------------------------------
// Login form
// ---------------------------------------------------------------------------

interface LoginFormAttrs {
    hide_register_link?: boolean
    on_forgot_password?: () => void
    on_login_success: (token: string) => Promise<void>
}

class LoginForm extends AuthLoginCommon<LoginFormAttrs> {
    on_login_success_callback: ((token: string) => Promise<void>) | null = null

    override oninit(vnode?: m.Vnode<LoginFormAttrs>) {
        if (vnode) {
            this.on_forgot_password_click = vnode.attrs.on_forgot_password
            this.on_login_success_callback = vnode.attrs.on_login_success
        }
    }

    override onupdate(vnode: m.Vnode<LoginFormAttrs>) {
        this.on_forgot_password_click = vnode.attrs.on_forgot_password
        this.on_login_success_callback = vnode.attrs.on_login_success
    }

    async login() {
        // The portal build defines MSI_THEME as its account's slug.
        const account_slug = process.env.MSI_THEME
        if (!is_one_of(Object.values(AccountSlug), account_slug)) {
            throw new Error(`MSI_THEME is not an account slug: ${String(account_slug)}`)
        }
        const data = {
            account_slug,
            password: this.data.form.password,
            username: this.data.form.email_address,
            username_support: this.data.form.email_support,
        }
        const {success, result: login_result, error_result} = await api.post('portal/login', data, true)
        if (!success) return this._handle_login_fail(error_result)
        if (login_result.otp_required) {
            this._handle_otp_required(login_result)
        } else if (login_result.token) {
            await this._handle_login_success(login_result.token)
        } else {
            notifier.notify($t('status.error.unknown'), 'danger')
        }
    }

    async verify_otp() {
        if (!this.data.pre_auth_token) return notifier.notify($t('status.error.unknown'), 'danger')
        const data: VerifyOTPRequest = {otp: this.data.form.otp_code, pre_auth_token: this.data.pre_auth_token}
        const {success, result: login_result, error_result} = await api.post('portal/verify-otp', data, true)
        if (!success) return this._handle_login_fail(error_result)
        if (login_result.token) {
            await this._handle_login_success(login_result.token)
        } else {
            notifier.notify($t('status.error.unknown'), 'danger')
        }
    }

    _handle_login_fail(error_result: ErrorResponse | undefined): void {
        const message = error_result?.detail
        let notify = $t('status.error.unknown')
        if (message === LoginErrorMessage.CREDENTIALS_INVALID || message === LoginErrorMessage.CREDENTIALS_INVALID_SUPPORT) {
            notify = $t('status.error.login.credentials_invalid')
        } else if (message === LoginErrorMessage.ACCOUNT_DISABLED) {
            notify = $t('status.error.login.account_disabled')
        } else if (message === LoginErrorMessage.ACCOUNT_DISABLED_SUPPORT) {
            notify = $t('status.error.login.account_disabled_support')
        } else if (
            message === LoginErrorMessage.TOKEN_EXPIRED ||
            message === LoginErrorMessage.TOKEN_MALFORMED ||
            message === LoginErrorMessage.OTP_NOT_SET_UP
        ) {
            notify = $t('status.error.login.pre_auth_token_expired')
            this.data.stepper.selection = 0
        } else if (message === LoginErrorMessage.OTP_INVALID) {
            this.data.otp_fail = message
            notify = $t('status.error.otp.invalid_notify')
        }
        notifier.notify(notify, 'danger', 7000)
        plausible_event(PlausibleEvent.ACCESS, {props: {type: 'access_login_fail'}})
    }

    _handle_otp_required(result: PortalLoginResponse): void {
        if (!result.token) {
            notifier.notify($t('status.error.unknown'), 'danger')
            return
        }
        this.data.pre_auth_token = result.token
        if (result.otp_secret_url) {
            this.data.otp_secret = result.otp_secret_url
            this.data.stepper.selection = 1
            plausible_event(PlausibleEvent.ACCESS, {props: {type: 'access_login_otp'}})
        } else {
            this.data.stepper.selection = 2
        }
    }

    async _handle_login_success(token: string) {
        if (this.on_login_success_callback) {
            await this.on_login_success_callback(token)
        }
        // Absent when the profile failed to load; '' is no supported language, which set_language ignores.
        void set_language($s.identity.user.language ?? '')
        notifier.notify($t('auth.login_success', {vendor: process.env.MSI_TITLE}), 'success')
        plausible_event(PlausibleEvent.ACCESS, {props: {type: 'access_login'}})
        void m.route.set(redirect_target() || '/')
    }

    override view(vnode?: m.Vnode<LoginFormAttrs>) {
        return (
            <div className='c-auth-login c-dialog-auth'>
                {super.view()}
                {vnode && !vnode.attrs.hide_register_link && (
                    <div className='dialog-auth-footer'>
                        <Button link={auth_href(AUTH_PATHS.register)} text={$t('profile.register')} type='info' variant='link' />
                    </div>
                )}
            </div>
        )
    }
}

// ---------------------------------------------------------------------------
// Dialog shell — the image panel + form panel chrome shared by every portal
// overlay dialog (auth, redeem, invite). The page content goes in the children.
// ---------------------------------------------------------------------------

type ImageText = string | {cite: string; quote: string}

/** The app's registration flow, which reports each step's image caption. */
type RegistrationComponentType = ComponentType<{on_step_change?: (step: number, image_text: ImageText) => void}>

/** CMS media-library asset shown in the dialog's image panel (built as `${config.media_photo_host}/${uri}`). */
const DIALOG_IMAGE_URI = 'v5/61d22418-06bd-4b30-9263-cebfbab2fd69'

export interface PortalDialogShellAttrs {
    /** Optional testimonial/caption shown over the image panel. */
    image_text?: ImageText
    /** Defaults to navigating home. */
    onclose?: (() => void) | undefined
}

export class PortalDialogShell extends MithrilComponent<PortalDialogShellAttrs> {
    view(vnode: m.Vnode<PortalDialogShellAttrs>) {
        const image_text = vnode.attrs.image_text || ''
        return (
            <div className='c-dialog-auth'>
                <Dialog
                    hideTitle
                    onclose={vnode.attrs.onclose ?? (() => m.route.set('/'))}
                    title={$t('landing.login.title', {vendor: process.env.MSI_TITLE})}
                    type='info'
                >
                    <div className='c-dialog-auth-content'>
                        <div className='dialog-auth-panel-image'>
                            <img alt='' src={`${config.media_photo_host}/${DIALOG_IMAGE_URI}`} />
                            {image_text ? (
                                <div className='dialog-auth-image-text'>
                                    {typeof image_text === 'string' ? (
                                        image_text
                                    ) : (
                                        <>
                                            <blockquote>{image_text.quote}</blockquote>
                                            <cite>{image_text.cite}</cite>
                                        </>
                                    )}
                                </div>
                            ) : null}
                        </div>
                        <div className='dialog-auth-panel-form on-chrome'>{vnode.children}</div>
                    </div>
                </Dialog>
            </div>
        )
    }
}

// ---------------------------------------------------------------------------
// Dialog content (tabs + active subview, all driven by the route)
// ---------------------------------------------------------------------------

interface DialogContentAttrs {
    on_login_success: (token: string) => Promise<void>
    /** Bubbles the registration step's image caption up to the shell. */
    on_registration_step?: (image_text: ImageText) => void
    RegistrationComponent?: RegistrationComponentType | undefined
    /** The auth route to render, which is not always the live one — see `PortalAuthDialog.rendered_uri`. */
    uri: string
}

class DialogContent extends MithrilComponent<DialogContentAttrs> {
    /** The login tab hosts the credentials / forgot-password / reset-password sub-views. */
    private login_subview(uri: string): 'credentials' | 'forgot' | 'reset' {
        if (uri === AUTH_PATHS.forgot) return 'forgot'
        if (uri === AUTH_PATHS.reset) return 'reset'
        return 'credentials'
    }

    /** Login tab body: credentials, or the forgot-password / reset-password sub-views. */
    private login_panel(uri: string, on_login_success: (token: string) => Promise<void>, show_register_link: boolean) {
        const subview = this.login_subview(uri)
        if (subview === 'forgot') {
            return (
                <div className='dialog-auth-forgot'>
                    <div className='dialog-auth-forgot-back'>
                        <Button
                            chrome={true}
                            icon='chevronLeft'
                            link={auth_href(AUTH_PATHS.login)}
                            size='d'
                            text={$t('profile.back_to_login')}
                        />
                    </div>
                    <AuthForgotPassword on_complete={() => m.route.set(auth_href(AUTH_PATHS.login))} />
                </div>
            )
        }
        if (subview === 'reset') {
            return (
                <div className='dialog-auth-forgot'>
                    <AuthResetPassword />
                </div>
            )
        }
        return (
            <LoginForm
                hide_register_link={show_register_link}
                on_forgot_password={() => m.route.set(auth_href(AUTH_PATHS.forgot))}
                on_login_success={on_login_success}
            />
        )
    }

    view(vnode: m.Vnode<DialogContentAttrs>) {
        const {on_login_success, on_registration_step, RegistrationComponent: Reg, uri} = vnode.attrs

        return (
            <Tabs className='dialog-auth-tabs' uri={uri}>
                <Tab
                    icon='login'
                    link={AUTH_PATHS.login}
                    route={[AUTH_PATHS.login, AUTH_PATHS.forgot, AUTH_PATHS.reset]}
                    text={$t('menu.login')}
                >
                    <TabPanel>{this.login_panel(uri, on_login_success, Boolean(Reg))}</TabPanel>
                </Tab>
                {Reg && (
                    <Tab
                        icon='edit'
                        link={AUTH_PATHS.register}
                        route={AUTH_PATHS.register}
                        text={$t('profile.register')}
                        type='brand'
                    >
                        <TabPanel>
                            <div className='dialog-auth-registration'>
                                {m(Reg, {
                                    on_step_change: (_step: number, image_text: ImageText) => on_registration_step?.(image_text),
                                })}
                            </div>
                        </TabPanel>
                    </Tab>
                )}
            </Tabs>
        )
    }
}

// ---------------------------------------------------------------------------
// Public API — a declarative, route-driven auth dialog. Render it (e.g. from a
// route overlay) whenever the current URL is an auth route.
// ---------------------------------------------------------------------------

export interface PortalAuthDialogAttrs {
    on_login_success: (token: string) => Promise<void>
    /** Defaults to navigating home; override when mounted outside the route (e.g. styleguide). */
    onclose?: () => void
    RegistrationComponent?: RegistrationComponentType
}

export class PortalAuthDialog extends MithrilComponent<PortalAuthDialogAttrs> {
    /** Caption shown over the image panel on the register tab; updated per registration step. */
    registration_image_text: ImageText = ''

    /**
     * The auth route the dialog shows, which lags `$s.env.uri` by exactly one navigation on the way out.
     *
     * A successful login routes to the home page, whose resolver readies the page before handing over
     * (`routes.ts`), and mithril keeps this dialog rendered until it does. `$s.env.uri` is already `/`
     * by then, so deriving the tab and sub-view from it would strip the dialog of its content — an empty
     * form panel, collapsing the dialog to its min-width — for as long as that takes. Holding the last
     * auth route lets the dialog leave the way it was.
     */
    private rendered_uri: string = AUTH_PATHS.login

    view(vnode: m.Vnode<PortalAuthDialogAttrs>) {
        const {RegistrationComponent: Reg, on_login_success, onclose} = vnode.attrs
        if (is_auth_route($s.env.uri)) {
            this.rendered_uri = $s.env.uri
        }
        const image_text =
            Reg && this.rendered_uri === AUTH_PATHS.register
                ? this.registration_image_text || {quote: $t('testimonial.quote'), cite: $t('testimonial.cite')}
                : ''

        return (
            <PortalDialogShell image_text={image_text} onclose={onclose}>
                <DialogContent
                    RegistrationComponent={Reg}
                    on_login_success={on_login_success}
                    on_registration_step={(text: ImageText) => {
                        if (this.registration_image_text !== text) {
                            this.registration_image_text = text
                            m.redraw()
                        }
                    }}
                    uri={this.rendered_uri}
                />
            </PortalDialogShell>
        )
    }
}
